import React from 'react';
import { ArrowRight, CheckCircle2, MessageSquareText } from 'lucide-react';
import FoxMark from './FoxMark';

const AboutPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-[#fbf8ef] text-[#17251d]">
      <nav className="border-b border-[#2f4a37] bg-[#102319]">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
          <a href="/" className="flex items-center gap-3 text-lg font-semibold text-[#fffdf7]">
            <FoxMark size="md" />
            AlgoFox
          </a>
          <div className="flex items-center gap-4">
            <a href="/privacy" className="hidden text-sm font-semibold text-[#dfe7d8] hover:text-white sm:inline">
              Privacy
            </a>
            <a href="/" className="text-sm font-semibold text-[#f0d7a6] hover:text-white">
              Back home
            </a>
          </div>
        </div>
      </nav>

      <section className="mx-auto max-w-4xl px-6 py-14">
        <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#c7652d]">About AlgoFox</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight text-[#1f3428] md:text-5xl">
          I am building the interview prep tool I kept wanting.
        </h1>
        <p className="mt-5 text-lg leading-8 text-[#52614f]">
          AlgoFox exists because coding interview prep should feel less like grinding an endless problem list and more
          like a focused coaching session.
        </p>

        <div className="mt-10 rounded-lg border border-[#d8decf] bg-white p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#fff1df] text-[#c7652d]">
              <MessageSquareText className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-[#1f3428]">Founder note</h2>
              <p className="mt-3 leading-7 text-[#52614f]">
                I currently work at a financial firm in NYC and previously worked at Meta. Over the years, I have
                prepared for and passed interview loops at companies including Meta, Google, Amazon, Stripe, and others,
                and I have conducted many coding interviews myself.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <section className="rounded-lg border border-[#d8decf] bg-[#fffdf7] p-6">
            <h2 className="text-xl font-semibold text-[#1f3428]">The repeated problem</h2>
            <p className="mt-3 leading-7 text-[#52614f]">
              Every time I prepared, the same questions came up: where do I start, how do I make a plan, what exactly
              should I study for each topic, and which solutions are actually useful for real interviews?
            </p>
          </section>

          <section className="rounded-lg border border-[#d8decf] bg-[#fffdf7] p-6">
            <h2 className="text-xl font-semibold text-[#1f3428]">What worked better</h2>
            <p className="mt-3 leading-7 text-[#52614f]">
              I had the most success when I stopped trying to memorize clever answers and focused on repetition across
              the basics: binary search, BFS, DFS, backtracking, and the other fundamentals that show up everywhere.
            </p>
          </section>
        </div>

        <section className="mt-8 rounded-lg border border-[#d8decf] bg-[#183222] p-6 text-[#fffdf7] shadow-sm">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white/10 text-[#f0d7a6]">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold">Why AlgoFox</h2>
              <p className="mt-3 leading-7 text-[#dfe7d8]">
                AlgoFox is meant to make practice feel closer to a real interview. You explain your plan, code while
                checking in with Foxy, run tests, and get a review that evaluates both your solution and your thought
                process.
              </p>
              <p className="mt-3 leading-7 text-[#dfe7d8]">
                The goal is not to spam random questions. It is to build intuition for core patterns until choosing the
                right approach starts to feel natural.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <a
            href="/#launch-list"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#d86f31] px-5 text-base font-semibold text-white transition hover:bg-[#c65d27] focus:outline-none focus:ring-2 focus:ring-[#f0c99e]"
          >
            Join the launch list
            <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href="/"
            className="inline-flex h-12 items-center justify-center rounded-lg border border-[#cfd8c4] bg-white px-5 text-base font-semibold text-[#31543f] transition hover:border-[#31543f]"
          >
            Back to homepage
          </a>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
